<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useSmarticoMiniGames } from '@/stores/smartico/miniGames';
import { useSmarticoApi } from '@/composables/useSmarticoApi';
import { useSmarticoInit } from '@/stores/smartico/init';
import { useAuthStore } from '@/stores/auth';
import { useAuth } from '@/composables/useAuth';

const route = useRoute();
const router = useRouter();
const { standardTitleSuffix } = getStandardSeoSuffixes();

const miniGameId = computed(() =>
  parseInt(String((route.params as { id: string }).id)),
);

const smarticoMiniGamesStore = useSmarticoMiniGames();
const { loadMiniGames } = smarticoMiniGamesStore;
const { miniGames } = storeToRefs(smarticoMiniGamesStore);
const { smarticoInstance } = useSmarticoApi();
const { isSuccessCheckUserIdentify } = storeToRefs(useSmarticoInit());

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);

const isLoading = ref(true);
const miniGameWrapper = ref<HTMLElement | null>(null);
const smarticoIframeRef = ref<HTMLIFrameElement | null>(null);

const currentMiniGame = computed(() => {
  return miniGames.value.find((game) => game.id === miniGameId.value);
});

useSeo({
  title: `${currentMiniGame.value?.name || 'Mini Game'} ${standardTitleSuffix}`,
});

onMounted(() => {
  // Mark that we're on a mini-game detail page, so when user goes back, filters can be restored
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem('casino_navigation_from_game', 'true');

      // If we didn't come from the mini-games list, clear the origin URL
      // This prevents incorrect navigation when going back from detail -> list -> back
      const referrer = document.referrer;
      const isFromMiniGamesList =
        referrer && referrer.includes('/vip/mini-games');

      if (!isFromMiniGamesList) {
        sessionStorage.removeItem('casino_origin_url');
      }
    } catch (e) {
      // Ignore storage errors
    }
  }
});

const { openLogin } = useAuth();

// Always navigate back to mini-games list from mini-game detail page
const handleBackNavigation = () => {
  router.push('/vip/mini-games');
};

const initializeMiniGame = async () => {
  if (!authStore.isAuthenticated) {
    openLogin();

    return;
  }

  if (isNaN(miniGameId.value)) {
    return;
  }

  if (miniGames.value.length === 0) {
    await loadMiniGames();
  }

  await new Promise<void>((resolve) => {
    const checkSmarticoInstanceReady = setInterval(() => {
      if (
        smarticoInstance.value &&
        typeof smarticoInstance.value.miniGame === 'function' &&
        isSuccessCheckUserIdentify.value
      ) {
        clearInterval(checkSmarticoInstanceReady);
        resolve();
      }
    }, 50);
  });

  await new Promise<void>((resolve) => {
    const checkIframeReady = setInterval(() => {
      if (smarticoIframeRef.value) {
        clearInterval(checkIframeReady);
        resolve();
      }
    }, 50);
  });

  const playOptions: {
    iframe?: string;
    inline?: boolean;
    height?: string;
    width?: string;
    zoom?: number;
    theme?: string;
  } = {
    iframe: 'smartico-minigame-frame',
    inline: true,
  };

  if (window._smartico && typeof window._smartico.miniGame === 'function') {
    window._smartico.miniGame(miniGameId.value, playOptions);
    if (smarticoIframeRef.value) {
      smarticoIframeRef.value.style.display = 'block';
      smarticoIframeRef.value.style.width = '153.85%';
      smarticoIframeRef.value.style.height = '100vh';
      smarticoIframeRef.value.style.transform = 'scale(0.65)';
      smarticoIframeRef.value.style.transformOrigin = 'top left';
      smarticoIframeRef.value.style.marginBottom = '-35vh';
    }
  }

  if (
    smarticoInstance.value &&
    typeof smarticoInstance.value.on === 'function'
  ) {
    smarticoInstance.value.on('gf_closing', () => {
      router.push('/');
    });
  }
};

watch(
  [isAuthenticated, smarticoInstance],
  async ([authenticated, smarticoApi]) => {
    if (!authenticated) {
      openLogin();
      router.push('/');

      return;
    }

    if (authenticated && smarticoApi) {
      isLoading.value = false;

      await initializeMiniGame();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="mini-game-page">
    <BaseScreenHeader
      heading-tag="h1"
      :on-back="handleBackNavigation"
      :title="currentMiniGame?.name || 'Mini-game'"
    />

    <div class="mini-game-main-content-wrapper">
      <div
        v-if="isLoading"
        class="loading-indicator text-texts text-[14px]"
      >
        Carregando mini-game...
      </div>
      <div
        v-else
        ref="miniGameWrapper"
        class="games mini-game-content-wrapper"
      >
        <section class="spacing">
          <div class="games_content">
            <div class="mini-game-iframe-container">
              <iframe
                id="smartico-minigame-frame"
                ref="smarticoIframeRef"
                class="smartico-minigame-iframe"
                title="Smartico Mini-game"
              ></iframe>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.mini-game-page {
  @apply flex flex-col;
}

.mini-game-main-content-wrapper {
  @apply w-full max-w-full;
}

.loading-indicator {
  @apply text-xl text-center my-10;
}

.games {
  @apply flex flex-col xl:pt-5;

  .spacing {
    @apply p-3 pt-0 px-0;
  }

  .games_content {
    @apply flex justify-center bg-bg-primary;

    .mini-game-iframe-container {
      width: 100%;
      height: auto;
      border: 1px solid #cccccc10;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .smartico-minigame-iframe {
      flex-grow: 1;
    }
  }
}
</style>
